import { v4 as uuid } from "uuid";
import notDeleted from "../notDeleted";

/**
 * @typedef {object} QuestionnaireCopyInput
 * @property {object} questionnaire - Source `Questionnaires` doc data (may include `docid`).
 * @property {Array<object>} [fields] - Source `QuestionnaireFields` docs (`QuestionnaireID === questionnaire.ID`).
 * @property {Array<object>} [options] - Source `MultipleQuestionOptions` docs (`Field === field.ID`).
 * @property {Array<object>} [rows] - Source `MatrixQuestionRows` docs (`Field === field.ID`).
 * @property {Array<object>} [columns] - Source `MatrixQuestionColumns` docs (`Field === field.ID`).
 * @property {string} client - Tenant CompagnyID to stamp on every copy.
 * @property {*} timestamp - Firestore Timestamp value for the copy's `Timestamp`.
 * @property {number} position - `Position` for the new questionnaire (usually list length + 1).
 * @property {() => string} [idFn] - ID generator, injectable for tests (defaults to uuid).
 */

/**
 * @typedef {object} QuestionnaireCopyResult
 * @property {object} questionnaire - New `Questionnaires` doc data.
 * @property {Array<object>} fields - New `QuestionnaireFields` docs.
 * @property {Array<object>} options - New `MultipleQuestionOptions` docs.
 * @property {Array<object>} rows - New `MatrixQuestionRows` docs.
 * @property {Array<object>} columns - New `MatrixQuestionColumns` docs.
 */

/**
 * @typedef {object} ShowWhenRule
 * @property {string} FieldID - Trigger question ID
 * @property {string} Operator - Comparison operator
 * @property {Array<string|number>} [Values] - Option/column IDs, or a threshold number
 * @property {string} [RowID] - Matrix row ID, for a matrix trigger
 */

const stripDocid = ({ docid: _docid, ...data }) => data;

/**
 * Point a show-when rule at the copied trigger question, options, columns and
 * row. IDs that are not in the maps (a threshold number, a trigger that was
 * deleted) stay as they are, so the rule behaves exactly as in the source.
 *
 * @param {ShowWhenRule | null | undefined} rule
 * @param {Map<string, string>} fieldIdMap
 * @param {Map<string, string>} answerIdMap - option + column IDs, old -> new
 * @param {Map<string, string>} rowIdMap
 * @returns {ShowWhenRule | null | undefined}
 */
const remapShowWhen = (rule, fieldIdMap, answerIdMap, rowIdMap) => {
  if (!rule) return rule;
  const remapped = {
    ...rule,
    FieldID: fieldIdMap.get(rule.FieldID) ?? rule.FieldID,
  };
  if (Array.isArray(rule.Values)) {
    remapped.Values = rule.Values.map((v) => answerIdMap.get(v) ?? v);
  }
  if (rule.RowID) remapped.RowID = rowIdMap.get(rule.RowID) ?? rule.RowID;
  return remapped;
};

/**
 * Build every document needed to duplicate a questionnaire.
 *
 * Generates fresh IDs and remaps every cross-reference to them: `QuestionnaireID`
 * and `SectionID` on the questions, `Field` on options/rows/columns, and the
 * show-when rule (`ShowWhen.FieldID`, `.Values`, `.RowID`). Everything else is
 * copied as-is, so meetstandaard provenance (`Standard`, `MSIId`, `posNeg`,
 * `StandaardVersion`) travels along.
 *
 * Dropped on purpose, like the single-question duplicate in
 * OrderQuestionnaireFieldsView: the effect link (`EffectId`, `Indicator`,
 * `IndicatorPosition`) — a meetstandaard effect has exactly one question field
 * in at most one questionnaire, and a second linked field would show up twice
 * in every per-effect reader — and `SyncId`, a portfolio pairing that belongs
 * to the source question only.
 * AI-CONTEXT: See docs/features/msi-effect-questionnaire-attach.md
 * Soft-deleted docs are dropped — they come from `.get()` bypass reads.
 *
 * Pure: performs no Firestore writes. The caller commits the returned docs.
 * Same shape as `buildIntakeFormCopy`.
 *
 * @param {QuestionnaireCopyInput} input
 * @returns {QuestionnaireCopyResult}
 */
const buildQuestionnaireCopy = ({
  questionnaire,
  fields = [],
  options = [],
  rows = [],
  columns = [],
  client,
  timestamp,
  position,
  idFn = uuid,
}) => {
  const tenant = { CompagnyID: client, Compagny: client, Timestamp: timestamp };
  const newQuestionnaireID = idFn();

  const newQuestionnaire = {
    ...stripDocid(questionnaire || {}),
    ...tenant,
    ID: newQuestionnaireID,
    Title: `${questionnaire?.Title || "Naamloos"} (kopie)`,
    Position: position,
  };

  const liveFields = (fields || []).filter(notDeleted);
  const fieldIdMap = new Map(liveFields.map((f) => [f.ID, idFn()]));

  // Children: drop deleted ones and those whose question was not copied.
  const copyChildren = (docs, idMap) =>
    (docs || [])
      .filter(notDeleted)
      .filter((d) => fieldIdMap.has(d.Field))
      .map((d) => {
        const newID = idFn();
        idMap.set(d.ID, newID);
        return { ...stripDocid(d), ...tenant, ID: newID, Field: fieldIdMap.get(d.Field) };
      });

  const answerIdMap = new Map();
  const rowIdMap = new Map();
  const newOptions = copyChildren(options, answerIdMap);
  const newColumns = copyChildren(columns, answerIdMap);
  const newRows = copyChildren(rows, rowIdMap);

  const newFields = liveFields.map((field) => {
    const {
      SyncId: _syncId,
      EffectId: _effectId,
      Indicator: _indicator,
      IndicatorPosition: _indicatorPosition,
      ...data
    } = stripDocid(field);
    const copy = {
      ...data,
      ...tenant,
      ID: fieldIdMap.get(field.ID),
      QuestionnaireID: newQuestionnaireID,
    };
    if (field.SectionID) copy.SectionID = fieldIdMap.get(field.SectionID) ?? field.SectionID;
    if (field.ShowWhen) copy.ShowWhen = remapShowWhen(field.ShowWhen, fieldIdMap, answerIdMap, rowIdMap);
    return copy;
  });

  return {
    questionnaire: newQuestionnaire,
    fields: newFields,
    options: newOptions,
    rows: newRows,
    columns: newColumns,
  };
};

export default buildQuestionnaireCopy;
